import { GUIDE_CONTENT } from '@hoedokloop/domain';
import { useEffect } from 'react';
import { useLocation } from 'react-router';

type RouteMetadata = {
  title: string;
  description: string;
  kind: 'WebApplication' | 'Article';
};

const defaultMetadata: RouteMetadata = {
  title: '회독루프 — 자격증 회독·오답 복습 플래너',
  description:
    '시험일과 교재 분량을 입력하면 회독 범위와 오답 복습을 한곳에서 관리하는 무가입 로컬 우선 자격증 공부 플래너',
  kind: 'WebApplication',
};

const routeMetadata: Record<string, RouteMetadata> = {
  ...Object.fromEntries(
    Object.entries(GUIDE_CONTENT).map(([slug, guide]) => [
      `/guides/${slug}`,
      {
        title: `${guide.seoTitle} | 회독루프`,
        description: guide.seoDescription,
        kind: 'Article' as const,
      },
    ]),
  ),
  '/privacy': {
    title: '개인정보 처리 안내 | 회독루프',
    description: '회독루프의 로컬 저장, 운영 이벤트, 선택 기능과 문의 방법을 안내합니다.',
    kind: 'Article',
  },
  '/terms': {
    title: '서비스 이용 안내 | 회독루프',
    description: '회독루프의 서비스 범위, 이용자 책임, 로컬 저장과 기능 변경 기준을 안내합니다.',
    kind: 'Article',
  },
  '/support': {
    title: '지원 안내 | 회독루프',
    description: '회독루프 문제 해결 방법과 안전한 문의 절차를 안내합니다.',
    kind: 'Article',
  },
};

function setMeta(selector: string, attributes: Record<string, string>): void {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement('meta');
    document.head.append(element);
  }
  for (const [name, value] of Object.entries(attributes)) element.setAttribute(name, value);
}

function setCanonical(url: string): void {
  let element = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!element) {
    element = document.createElement('link');
    element.rel = 'canonical';
    document.head.append(element);
  }
  element.href = url;
}

export function SeoMetadata() {
  const { pathname } = useLocation();

  useEffect(() => {
    const metadata = routeMetadata[pathname] ?? defaultMetadata;
    const url = new URL(pathname, window.location.origin).toString();
    document.title = metadata.title;
    setMeta('meta[name="description"]', { name: 'description', content: metadata.description });
    setMeta('meta[property="og:title"]', { property: 'og:title', content: metadata.title });
    setMeta('meta[property="og:description"]', {
      property: 'og:description',
      content: metadata.description,
    });
    setMeta('meta[property="og:type"]', {
      property: 'og:type',
      content: metadata.kind === 'Article' ? 'article' : 'website',
    });
    setMeta('meta[property="og:url"]', { property: 'og:url', content: url });
    const imageUrl = new URL('/og-hoedokloop.png', window.location.origin).toString();
    setMeta('meta[property="og:image"]', { property: 'og:image', content: imageUrl });
    setMeta('meta[property="og:image:width"]', { property: 'og:image:width', content: '1200' });
    setMeta('meta[property="og:image:height"]', { property: 'og:image:height', content: '630' });
    setMeta('meta[property="og:image:alt"]', {
      property: 'og:image:alt',
      content: '과목별 회독 지도가 그려진 회독루프 화면',
    });
    setMeta('meta[name="twitter:card"]', {
      name: 'twitter:card',
      content: 'summary_large_image',
    });
    setMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: imageUrl });
    setMeta('meta[name="twitter:image:alt"]', {
      name: 'twitter:image:alt',
      content: '과목별 회독 지도가 그려진 회독루프 화면',
    });
    setCanonical(url);

    let script = document.head.querySelector<HTMLScriptElement>('#pn-structured-data');
    if (!script) {
      script = document.createElement('script');
      script.id = 'pn-structured-data';
      script.type = 'application/ld+json';
      document.head.append(script);
    }
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': metadata.kind,
      name: metadata.title,
      description: metadata.description,
      url,
      image: imageUrl,
      inLanguage: 'ko-KR',
      ...(metadata.kind === 'WebApplication'
        ? { applicationCategory: 'EducationalApplication', operatingSystem: 'Web' }
        : {}),
    });
  }, [pathname]);

  return null;
}
